import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { VehiclePosition, ConnectionStatus, ChannelSubscription } from '../../types/websocket';

export interface WebSocketState {
  connection: ConnectionStatus;
  vehiclePositions: Record<string, VehiclePosition>;
  vehicleStatuses: Record<string, any>;
  alerts: any[];
  channels: Record<string, ChannelSubscription>;
  shouldAutoReconnect: boolean;
}

const initialState: WebSocketState = {
  connection: {
    isConnected: false,
    clientId: null,
    error: null,
    reconnectAttempts: 0,
  },
  vehiclePositions: {},
  vehicleStatuses: {},
  alerts: [],
  channels: {},
  shouldAutoReconnect: true,
};

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      state.connection = action.payload;
    },
    addVehiclePosition: (state, action: PayloadAction<VehiclePosition>) => {
      const incoming = action.payload;
      const existing = state.vehiclePositions[incoming.vin];

      // CRIT-10: Deduplicate — skip if timestamp is same or older
      if (
        existing &&
        existing.timestamp &&
        incoming.timestamp &&
        new Date(incoming.timestamp).getTime() <=
          new Date(existing.timestamp).getTime()
      ) {
        return;
      }

      state.vehiclePositions[incoming.vin] = incoming;

      // CRIT-9: Prune stale entries (older than 30 minutes) when map gets large
      const keys = Object.keys(state.vehiclePositions);
      if (keys.length > 5000) {
        const thirtyMinAgo = Date.now() - 30 * 60 * 1000;
        for (const vin of keys) {
          const pos = state.vehiclePositions[vin];
          if (
            pos.timestamp &&
            new Date(pos.timestamp).getTime() < thirtyMinAgo
          ) {
            delete state.vehiclePositions[vin];
          }
        }
      }
    },
    updateVehicleStatus: (state, action: PayloadAction<any>) => {
      if (action.payload.vin) {
        state.vehicleStatuses[action.payload.vin] = action.payload;
      }
    },
    addAlert: (state, action: PayloadAction<any>) => {
      state.alerts.unshift(action.payload);
      if (state.alerts.length > 100) {
        state.alerts = state.alerts.slice(0, 100);
      }
    },
    setChannelInfo: (state, action: PayloadAction<ChannelSubscription>) => {
      state.channels[action.payload.channel] = action.payload;
    },
    clearAlerts: (state) => {
      state.alerts = [];
    },
    setShouldAutoReconnect: (state, action: PayloadAction<boolean>) => {
      state.shouldAutoReconnect = action.payload;
    },
    resetWebSocketData: (state) => {
      state.vehiclePositions = {};
      state.vehicleStatuses = {};
      state.alerts = [];
      state.channels = {};
      state.connection = initialState.connection;
    },
  },
});

export const {
  setConnectionStatus,
  addVehiclePosition,
  updateVehicleStatus,
  addAlert,
  setChannelInfo,
  clearAlerts,
  setShouldAutoReconnect,
  resetWebSocketData,
} = websocketSlice.actions;

export default websocketSlice.reducer;